import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { HealthSource, Tool } from '@ace-tracker/shared';
import { fetchDashboardSummary, fetchEfficiencySummary, fetchExecutiveSummary, fetchFilterOptions, fetchOverviewSummary, fetchRtkDetailSummary, fetchSessionsSummary, fetchSpendSummary, fetchTokenOptimizationSummary, UnauthorizedError } from './api';
import { useAuth } from './auth';
import { AppShell } from './components/AppShell';
import { ClaudeCodeDashboardBody } from './components/ClaudeCodeDashboardBody';
import { CostFilterBar } from './components/CostFilterBar';
import { SessionsDashboardBody } from './components/SessionsDashboardBody';
import { CodexDashboardBody } from './components/CodexDashboardBody';
import { KiroDashboardBody } from './components/KiroDashboardBody';
import { GeminiDashboardBody } from './components/GeminiDashboardBody';
import { CopilotDashboardBody } from './components/CopilotDashboardBody';
import { DataFreshness } from './components/DataFreshness';
import { DataHealthBody } from './components/DataHealthBody';
import { DataQualityBanner } from './components/DataQualityBanner';
import { EfficiencyDashboardBody } from './components/EfficiencyDashboardBody';
import { ExportMenu } from './components/ExportMenu';
import { ExecutiveDashboardBody } from './components/executive/ExecutiveDashboardBody';
import { CoverageChip, DownloadSlideButton } from './components/executive/ExecutiveHeaderActions';
import { formatPeriod } from './components/executive/format';
import { FilterBar } from './components/FilterBar';
import { InfoTooltip } from './components/InfoTooltip';
import { OverviewDashboardBody } from './components/OverviewDashboardBody';
import { TokenOptimizationDashboardBody } from './components/TokenOptimizationDashboardBody';
import { GraphifyDashboardBody } from './components/GraphifyDashboardBody';
import { HeadroomDashboardBody } from './components/HeadroomDashboardBody';
import { PageHeader } from './components/PageHeader';
import { RtkDashboardBody } from './components/RtkDashboardBody';
import { SettingsPage } from './components/settings/SettingsPage';
import { SpendDashboardBody } from './components/SpendDashboardBody';
import { SourceEmptyState } from './components/SourceEmptyState';
import { TOOL_LABELS } from './components/Sidebar';
import { PRODUCT_NAME } from './components/Wordmark';
import { INDICATIVE_COST_DEFINITION, METRIC_INFO } from './metricInfo';
import { sourceHealth, useDataHealth, type SourceHealthInput } from './health';
import { useDashboardLimits } from './limits';
import { clampCustomRange, presetToRange, type RangePreset } from './ranges';
import { DeveloperName, useDeveloperProfiles, useSettings } from './settings';
import { isViewEnabled, useFeatureFlags } from './features';
import { COST_VIEWS, DEFAULT_VIEW, isToolView, useDashboardUrlState, type ViewMode } from './urlState';

/** The Executive view compares periods, which is meaningless for a day or a week. */
const EXECUTIVE_PRESETS: RangePreset[] = ['M', 'Q', 'Y', 'C'];

export function Dashboard() {
  const { username, logout } = useAuth();
  const [{ view, preset: urlPreset, customRange, developerId, filters }, updateUrlState] = useDashboardUrlState();
  const isExecutive = view === 'executive';
  const preset = isExecutive && !EXECUTIVE_PRESETS.includes(urlPreset) ? 'M' : urlPreset;
  // The API rejects a range reaching past data retention, so requests wait
  // for the limit and are held inside it (the filter bar flags a limited range).
  const { data: limits, isPending: limitsPending } = useDashboardLimits();
  const maxLookbackDays = limits?.maxLookbackDays;
  const range = preset === 'C' ? clampCustomRange(customRange, maxLookbackDays) : presetToRange(preset, maxLookbackDays);
  const isOverview = view === 'overview';
  const isSpend = view === 'spend';
  const isEfficiency = view === 'efficiency';
  const isSessions = view === 'sessions';
  const isCostView = COST_VIEWS.includes(view);
  const isOptimization = view === 'optimization';
  const isRtk = view === 'rtk';
  const isHeadroom = view === 'headroom';
  const isGraphify = view === 'graphify';
  // The optimizer overview, Headroom, and Graphify screens all render from the same optimization summary.
  const usesOptimizationSummary = isOptimization || isHeadroom || isGraphify;
  const isSettings = view === 'settings';
  const isHealth = view === 'health';
  const isTool = isToolView(view);
  const { profile } = useDeveloperProfiles();
  const { data: settings } = useSettings();
  const selectedTeam = developerId?.startsWith('team:') ? developerId.slice(5) : undefined;
  const selectedTeamDeveloperIds = selectedTeam === undefined || !settings
    ? undefined
    : settings.developers
        .filter((developer) => developer.team?.toLocaleLowerCase() === selectedTeam.toLocaleLowerCase())
        .map((developer) => developer.developerId);
  const selectedDeveloperId = selectedTeam === undefined ? developerId : undefined;

  useEffect(() => {
    if (selectedTeam !== undefined && settings && !settings.developers.some(
      (developer) => developer.team?.toLowerCase() === selectedTeam.toLowerCase(),
    )) {
      updateUrlState({ developerId: undefined });
    }
  }, [selectedTeam, settings, updateUrlState]);

  // A view an admin has switched off (bookmark, shared link, or a flag flipped while open)
  // falls back to the default view. Replaces the history entry so Back doesn't land on it again.
  const { flags, loaded: flagsLoaded } = useFeatureFlags();
  const viewDisabled = flagsLoaded && !isViewEnabled(view, flags);
  useEffect(() => {
    if (viewDisabled) updateUrlState({ view: DEFAULT_VIEW, developerId: undefined }, { replace: true });
  }, [viewDisabled, updateUrlState]);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['dashboard-summary', view, developerId, selectedTeamDeveloperIds, preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchDashboardSummary({ tool: view as Tool, developerId: selectedDeveloperId, developerIds: selectedTeamDeveloperIds, from: range.from, to: range.to }),
    // Keep the last result on screen (filter bar included) while a filter change
    // refetches — but only within the same view, never another tool's data.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === view ? previous : undefined),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isTool && (selectedTeam === undefined || selectedTeamDeveloperIds !== undefined),
  });

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    error: overviewError,
    isFetching: isOverviewFetching,
    refetch: refetchOverview,
  } = useQuery({
    queryKey: ['dashboard-overview', preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchOverviewSummary({ from: range.from, to: range.to }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isOverview,
  });

  const {
    data: spendData,
    isLoading: isSpendLoading,
    isError: isSpendError,
    error: spendError,
    isFetching: isSpendFetching,
    refetch: refetchSpend,
  } = useQuery({
    queryKey: ['dashboard-spend', preset, preset === 'C' ? customRange : null, maxLookbackDays, filters],
    queryFn: () => fetchSpendSummary({ from: range.from, to: range.to }, filters),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isSpend,
  });

  const {
    data: efficiencyData,
    isLoading: isEfficiencyLoading,
    isError: isEfficiencyError,
    error: efficiencyError,
    isFetching: isEfficiencyFetching,
    refetch: refetchEfficiency,
  } = useQuery({
    queryKey: ['dashboard-efficiency', preset, preset === 'C' ? customRange : null, maxLookbackDays, filters],
    queryFn: () => fetchEfficiencySummary({ from: range.from, to: range.to }, filters),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isEfficiency,
  });

  const {
    data: sessionsData,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
    error: sessionsError,
    isFetching: isSessionsFetching,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: ['dashboard-sessions', preset, preset === 'C' ? customRange : null, maxLookbackDays, filters],
    queryFn: () => fetchSessionsSummary({ from: range.from, to: range.to }, filters),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isSessions,
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['dashboard-filter-options', preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchFilterOptions({ from: range.from, to: range.to }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isCostView,
  });

  const {
    data: optimizationData,
    isLoading: isOptimizationLoading,
    isError: isOptimizationError,
    error: optimizationError,
    isFetching: isOptimizationFetching,
    refetch: refetchOptimization,
  } = useQuery({
    queryKey: ['dashboard-token-optimization', developerId, selectedTeamDeveloperIds, preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchTokenOptimizationSummary({ from: range.from, to: range.to, developerId: selectedDeveloperId, developerIds: selectedTeamDeveloperIds }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && usesOptimizationSummary && (selectedTeam === undefined || selectedTeamDeveloperIds !== undefined),
  });

  const {
    data: rtkData,
    isLoading: isRtkLoading,
    isError: isRtkError,
    error: rtkError,
    isFetching: isRtkFetching,
    refetch: refetchRtk,
  } = useQuery({
    queryKey: ['dashboard-rtk', developerId, selectedTeamDeveloperIds, preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchRtkDetailSummary({ from: range.from, to: range.to, developerId: selectedDeveloperId, developerIds: selectedTeamDeveloperIds }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isRtk && (selectedTeam === undefined || selectedTeamDeveloperIds !== undefined),
  });

  const {
    data: executiveData,
    isLoading: isExecutiveLoading,
    isError: isExecutiveError,
    error: executiveError,
    isFetching: isExecutiveFetching,
    refetch: refetchExecutive,
  } = useQuery({
    queryKey: ['dashboard-executive', preset, preset === 'C' ? customRange : null, maxLookbackDays],
    queryFn: () => fetchExecutiveSummary({ from: range.from, to: range.to }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
    enabled: !limitsPending && isExecutive,
  });

  // Data health backs the header's freshness line, the Executive confidence
  // strip, the empty states, and the Data health page. Every view still
  // renders if it is loading or fails.
  const {
    data: healthData,
    isLoading: isHealthLoading,
    isError: isHealthError,
    error: healthError,
    isFetching: isHealthFetching,
    refetch: refetchHealth,
  } = useDataHealth(range, [preset, preset === 'C' ? customRange : null, maxLookbackDays], !limitsPending && !isSettings);
  const healthOf = (source: HealthSource): SourceHealthInput => (isHealthLoading ? 'loading' : sourceHealth(healthData, source));
  // Which sources the current view shows, for "Data as of"; undefined = any AI tool.
  const viewSources: HealthSource[] | undefined = isTool
    ? [view]
    : isRtk ? ['rtk'] : isHeadroom ? ['headroom'] : isGraphify ? ['graphify'] : isOptimization ? ['rtk', 'headroom', 'graphify'] : isSpend || isEfficiency || isSessions ? ['claude-code', 'codex'] : undefined;

  if (
    (isHealthError && healthError instanceof UnauthorizedError) ||
    (isError && error instanceof UnauthorizedError) ||
    (isOverviewError && overviewError instanceof UnauthorizedError) ||
    (isSpendError && spendError instanceof UnauthorizedError) ||
    (isEfficiencyError && efficiencyError instanceof UnauthorizedError) ||
    (isSessionsError && sessionsError instanceof UnauthorizedError) ||
    (isOptimizationError && optimizationError instanceof UnauthorizedError) ||
    (isRtkError && rtkError instanceof UnauthorizedError) ||
    (isExecutiveError && executiveError instanceof UnauthorizedError)
  ) {
    logout();
    return null;
  }

  // Switching views changes which fields exist, so clear a developer filter
  // that only made sense for the previous tool's hash space.
  function handleViewChange(next: ViewMode) {
    updateUrlState({ view: next, developerId: undefined });
  }

  const setDeveloperId = (id: string | undefined) => updateUrlState({ developerId: id });
  const optimizationDevelopers = optimizationData
    ? (isHeadroom ? optimizationData.headroom : isGraphify ? optimizationData.graphify : [...optimizationData.developers, ...optimizationData.headroom, ...optimizationData.graphify])
        .map((point) => point.developerId)
        .filter((id, index, ids) => ids.indexOf(id) === index)
        .map((developerId) => ({ developerId }))
    : undefined;
  const rtkDevelopers = rtkData
    ? [...rtkData.developers, ...rtkData.lifetimeByDeveloper]
        .map((point) => point.developerId)
        .filter((id, index, ids) => ids.indexOf(id) === index)
        .map((developerId) => ({ developerId }))
    : undefined;

  return (
    <AppShell
      view={view}
      flags={flags}
      onViewChange={handleViewChange}
      username={username}
      onLogout={logout}
      fitViewport={isExecutive}
      filterBar={({ onOpen, buttonRef }) =>
        isExecutive ? (
          <FilterBar
            title="Executive Summary"
            subtitle={executiveData ? formatPeriod(executiveData) : undefined}
            isFetching={isExecutiveFetching}
            onRefresh={() => void refetchExecutive()}
            preset={preset}
            onPresetChange={(p) => updateUrlState({ preset: p })}
            rangePresets={EXECUTIVE_PRESETS}
            customRange={customRange}
            onCustomRangeChange={(r) => updateUrlState({ customRange: r })}
            developerId={undefined}
            onDeveloperChange={setDeveloperId}
            onOpenMenu={onOpen}
            menuButtonRef={buttonRef}
            actions={
              <>
                {executiveData && !healthData && <CoverageChip summary={executiveData} />}
                <DownloadSlideButton summary={executiveData} />
              </>
            }
          />
        ) : isSettings ? (
          <PageHeader
            title="Settings"
            subtitle="Developer names, model pricing, cost, and account"
            onOpenMenu={onOpen}
            menuButtonRef={buttonRef}
          />
        ) : (
          <FilterBar
            title={isHealth ? 'Data Health' : isOverview ? 'All Tools Overview' : isSpend ? 'Spend' : isEfficiency ? 'Efficiency' : isSessions ? 'Sessions' : isOptimization ? 'Token Optimization' : isRtk ? 'RTK Token Savings' : isHeadroom ? 'Headroom Context Compression' : isGraphify ? 'Graphify Knowledge Graphs' : `${TOOL_LABELS[view as Tool]} Adoption`}
            generatedAt={isOverview ? overviewData?.generatedAt : isSpend ? spendData?.generatedAt : isEfficiency ? efficiencyData?.generatedAt : isSessions ? sessionsData?.generatedAt : usesOptimizationSummary ? optimizationData?.generatedAt : isRtk ? rtkData?.generatedAt : data?.generatedAt}
            freshness={healthData ? <DataFreshness health={healthData} sources={viewSources} /> : undefined}
            isFetching={isHealth ? isHealthFetching : isOverview ? isOverviewFetching : isSpend ? isSpendFetching : isEfficiency ? isEfficiencyFetching : isSessions ? isSessionsFetching : usesOptimizationSummary ? isOptimizationFetching : isRtk ? isRtkFetching : isFetching}
            onRefresh={() => {
              void refetchHealth();
              if (!isHealth) void (isOverview ? refetchOverview() : isSpend ? refetchSpend() : isEfficiency ? refetchEfficiency() : isSessions ? refetchSessions() : usesOptimizationSummary ? refetchOptimization() : isRtk ? refetchRtk() : refetch());
            }}
            preset={preset}
            onPresetChange={(p) => updateUrlState({ preset: p })}
            customRange={customRange}
            onCustomRangeChange={(r) => updateUrlState({ customRange: r })}
            developers={isOverview || isCostView || isHealth ? undefined : usesOptimizationSummary ? optimizationDevelopers : isRtk ? rtkDevelopers : data?.developers}
            developerId={developerId}
            onDeveloperChange={setDeveloperId}
            onOpenMenu={onOpen}
            menuButtonRef={buttonRef}
          />
        )
      }
    >
      {isSettings && <SettingsPage />}

      {isExecutive && isExecutiveLoading && <p className="font-light text-text-muted">Loading executive summary…</p>}
      {isExecutive && isExecutiveError && !(executiveError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load the executive summary: {(executiveError as Error).message}</p>
      )}
      {isExecutive && executiveData && (
        <ExecutiveDashboardBody
          summary={executiveData}
          confidence={healthData?.confidence}
          onOpenSettings={() => handleViewChange('settings')}
          onOpenHealth={() => handleViewChange('health')}
        />
      )}

      {isHealth && isHealthError && !(healthError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load data health: {(healthError as Error).message}</p>
      )}
      {isHealth && healthData && <DataHealthBody health={healthData} range={range} SectionHeading={SectionHeading} />}

      {!isSettings && !isExecutive && (isHealth ? isHealthLoading : isOverview ? isOverviewLoading : isSpend ? isSpendLoading : isEfficiency ? isEfficiencyLoading : isSessions ? isSessionsLoading : usesOptimizationSummary ? isOptimizationLoading : isRtk ? isRtkLoading : isLoading) &&<p className="font-light text-text-muted">Loading dashboard data…</p>}
      {isTool && isError && !(error instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load dashboard: {(error as Error).message}</p>
      )}
      {isOverview && isOverviewError && !(overviewError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load overview: {(overviewError as Error).message}</p>
      )}
      {isSpend && isSpendError && !(spendError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load spend: {(spendError as Error).message}</p>
      )}
      {isCostView && (
        <CostFilterBar filters={filters} options={filterOptions} onChange={(next) => updateUrlState({ filters: next })} />
      )}
      {isSpend && spendData && <SpendDashboardBody summary={spendData} formatCurrency={formatCurrency} SectionHeading={SectionHeading} />}
      {isEfficiency && isEfficiencyError && !(efficiencyError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load efficiency: {(efficiencyError as Error).message}</p>
      )}
      {isEfficiency && efficiencyData && (
        <EfficiencyDashboardBody summary={efficiencyData} formatCurrency={formatCurrency} SectionHeading={SectionHeading} />
      )}
      {isSessions && isSessionsError && !(sessionsError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load sessions: {(sessionsError as Error).message}</p>
      )}
      {isSessions && sessionsData && (
        <SessionsDashboardBody summary={sessionsData} formatCurrency={formatCurrency} SectionHeading={SectionHeading} />
      )}
      {usesOptimizationSummary && isOptimizationError && !(optimizationError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load token optimization data: {(optimizationError as Error).message}</p>
      )}

      {isRtk && isRtkError && !(rtkError instanceof UnauthorizedError) && (
        <p className="font-medium text-error">Failed to load RTK data: {(rtkError as Error).message}</p>
      )}

      {isOptimization && optimizationData && (
        <TokenOptimizationDashboardBody summary={optimizationData} SectionHeading={SectionHeading} onSelectOptimizer={handleViewChange} />
      )}
      {isHeadroom && optimizationData && (
        <HeadroomDashboardBody summary={optimizationData} SectionHeading={SectionHeading} health={healthOf('headroom')} />
      )}
      {isGraphify && optimizationData && (
        <GraphifyDashboardBody summary={optimizationData} SectionHeading={SectionHeading} health={healthOf('graphify')} />
      )}

      {isRtk && rtkData && <RtkDashboardBody summary={rtkData} SectionHeading={SectionHeading} health={healthOf('rtk')} />}

      {isOverview && overviewData && (
        <OverviewDashboardBody
          summary={overviewData}
          formatCurrency={formatCurrency}
          SectionHeading={SectionHeading}
          onSelectTool={handleViewChange}
        />
      )}

      {/* Nobody reported usage for this tool in the range: say why, rather than a page of zeros. */}
      {isTool && data && data.developers.length === 0 && (
        <SourceEmptyState source={view as Tool} health={healthOf(view as Tool)} />
      )}

      {isTool && data && data.developers.length > 0 && (
        <>
          {data.toolData.tool === 'copilot' ? (
            <CopilotDashboardBody
              toolData={data.toolData}
              developersCount={data.developers.length}
              coverage={data.coverage}
              formatCurrency={formatCurrency}
              SectionHeading={SectionHeading}
            />
          ) : data.toolData.tool === 'claude-code' ? (
            <ClaudeCodeDashboardBody
              toolData={data.toolData}
              developersCount={data.developers.length}
              coverage={data.coverage}
              formatCurrency={formatCurrency}
              SectionHeading={SectionHeading}
            />
          ) : data.toolData.tool === 'codex' ? (
            <CodexDashboardBody
              toolData={data.toolData}
              developersCount={data.developers.length}
              coverage={data.coverage}
              formatCurrency={formatCurrency}
              SectionHeading={SectionHeading}
            />
          ) : data.toolData.tool === 'gemini' ? (
            <GeminiDashboardBody
              toolData={data.toolData}
              developersCount={data.developers.length}
              coverage={data.coverage}
              formatCurrency={formatCurrency}
              SectionHeading={SectionHeading}
            />
          ) : (
            <KiroDashboardBody
              toolData={data.toolData}
              developersCount={data.developers.length}
              coverage={data.coverage}
              SectionHeading={SectionHeading}
            />
          )}

          <section>
            <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
              <ExportMenu title="Developers" className="float-right -mr-2 -mt-2 ml-2" />
              <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
                Developers
                <InfoTooltip text={METRIC_INFO.developers} />
              </h3>
              <p className="mt-1 text-xs text-text-muted">Everyone with usage in this range.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                    <tr>
                      <th className="pb-2.5 pr-3">Developer</th>
                      <th className="pb-2.5 pr-3">Team</th>
                      <th className="pb-2.5 pr-3 text-right">Events</th>
                      <th className="pb-2.5 pr-3 text-right">Devices</th>
                      <th className="pb-2.5 text-right">Last active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.developers.map((dev) => (
                      <tr key={dev.developerId} className="border-b border-border last:border-0">
                        <td className="py-2.5 pr-3">
                          <DeveloperName developerId={dev.developerId} />
                        </td>
                        <td className="py-2.5 pr-3 text-text-muted">{profile(dev.developerId)?.team ?? '—'}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{dev.events}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{dev.devices}</td>
                        <td className="py-2.5 text-right tabular-nums text-text-muted">
                          {new Date(dev.lastActive).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {data.developers.length === 0 && (
                      <tr>
                        <td className="py-3 text-text-muted" colSpan={5}>
                          No developers reported usage in this range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <DataQualityBanner findings={data.dataQuality} />

          <footer className="flex flex-wrap gap-3.5 border-t border-border pt-4 text-xs text-text-faint">
            <span>{PRODUCT_NAME}</span>
            <span>{data.developers.length} developers</span>
            <span>{data.coverage.coveragePercent}% reporting coverage</span>
            <span>Figures de-duplicated and counter-corrected</span>
            <span title={INDICATIVE_COST_DEFINITION}>Estimated cost is indicative, not an invoice</span>
          </footer>
        </>
      )}
    </AppShell>
  );
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function SectionHeading({ title, hint, info }: { title: string; hint?: string; info?: string }) {
  return (
    <div className="mb-4 flex flex-wrap items-baseline gap-3">
      <h2 className="flex items-center gap-1.5 text-[13px] font-medium uppercase tracking-[0.9px] text-text-muted">
        {title}
        {info && <InfoTooltip text={info} />}
      </h2>
      {hint && <span className="text-xs text-text-faint">{hint}</span>}
    </div>
  );
}
